import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  FiCheck,
  FiFileText,
  FiImage,
  FiInfo,
  FiLoader,
  FiPlus,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { PdfPanelContext } from "../../../../components/MessageWithTableRenderer";
import {
  formatBytes,
  formatLimit,
  isGone,
  isReady,
  RETRY_USELESS,
} from "./attachmentState";
import {
  ensureLocalFile,
  hasOriginalLoader,
  localFile,
  localFilesVersion,
  subscribeLocalFiles,
} from "./localFiles";

export function formatExpiry(expiresAt, now, language = "zh") {
  const left = Date.parse(expiresAt) - (now ?? Date.now());
  if (!Number.isFinite(left)) return "";
  const en = language === "en";
  if (left <= 0) return en ? "expired" : "已到期";
  const minutes = Math.ceil(left / 60000);
  if (minutes >= 120)
    return en
      ? `expires in ~${Math.round(minutes / 60)} h`
      : `約 ${Math.round(minutes / 60)} 小時後到期`;
  return en ? `expires in ~${minutes} min` : `約 ${minutes} 分鐘後到期`;
}
// Local checks and Root rejections share these codes; anything else keeps the wording Root sent.
const ERROR_TEXT = {
  UNSUPPORTED_MEDIA_TYPE: {
    zh: "不支援的格式，可接受 PNG、JPEG、WEBP、PDF。",
    en: "Unsupported format. Accepted: PNG, JPEG, WEBP, PDF.",
  },
  FILE_BYTES_EXCEEDED: { zh: "檔案太大。", en: "The file is too large." },
  SESSION_BYTES_EXCEEDED: {
    zh: "超過本對話的容量。",
    en: "This chat's storage limit would be exceeded.",
  },
  TURN_BYTES_EXCEEDED: {
    zh: "本輪選取的檔案太大，請取消部分檔案。",
    en: "The selected files are too large for one message; deselect some.",
  },
  REQUEST_BYTES_EXCEEDED: {
    zh: "這次請求太大。",
    en: "The request is too large.",
  },
  IMAGE_PIXELS_EXCEEDED: {
    zh: "圖片解析度太高。",
    en: "The image resolution is too high.",
  },
  INVALID_FILE: {
    zh: "檔案無法讀取，可能已損毀。",
    en: "The file could not be read; it may be damaged.",
  },
  ENCRYPTED_PDF: {
    zh: "PDF 有加密，無法讀取。",
    en: "The PDF is encrypted and cannot be read.",
  },
  PDF_SAFETY_LIMIT: {
    zh: "PDF 頁數過多，無法處理。",
    en: "The PDF has too many pages to process.",
  },
  ATTACHMENT_EXPIRED: {
    zh: "原檔已過期，請重新上傳。",
    en: "A file has expired; please upload it again.",
  },
  ATTACHMENT_REMOVED: {
    zh: "原檔已被移除，請重新上傳。",
    en: "A file was removed; please upload it again.",
  },
  ATTACHMENT_NOT_FOUND: { zh: "找不到附件。", en: "Attachment not found." },
  ATTACHMENT_NOT_READY: {
    zh: "附件尚未準備好。",
    en: "The attachment is not ready yet.",
  },
  SESSION_NOT_FOUND: { zh: "找不到這個對話。", en: "Conversation not found." },
  MODEL_NOT_SUPPORTED: {
    zh: "這個模型不支援附件，請改選其他模型。",
    en: "This model does not support attachments; pick another model.",
  },
  ATTACHMENT_VIDEO_CONFLICT: {
    zh: "附件不能與影片搜尋同時使用，請關閉影片搜尋或取消附件。",
    en: "Attachments cannot be combined with video search; turn video search off or deselect the files.",
  },
  ATTACHMENTS_NOT_SUPPORTED: {
    zh: "這個環境目前不支援附件提問。",
    en: "Attachment questions are not available here.",
  },
  UNAUTHENTICATED: { zh: "請重新登入。", en: "Please sign in again." },
  ATTACHMENT_BUSY: {
    zh: "檔案處理忙碌中，請稍後再試。",
    en: "File processing is busy; please try again shortly.",
  },
  STORAGE_UNAVAILABLE: {
    zh: "附件儲存暫時無法使用，請稍後重試。",
    en: "Attachment storage is temporarily unavailable; please retry.",
  },
  STORAGE_NOT_CONFIGURED: {
    zh: "附件儲存尚未設定。",
    en: "Attachment storage is not configured.",
  },
  STORAGE_DATABASE_REJECTED: {
    zh: "附件儲存設定有誤。",
    en: "Attachment storage is misconfigured.",
  },
  SCHEMA_NOT_READY: {
    zh: "附件資料庫尚未就緒。",
    en: "The attachment database is not ready.",
  },
  UPLOAD_INTERRUPTED: { zh: "上傳被中斷。", en: "The upload was interrupted." },
  ATTACHMENT_DEADLINE_EXCEEDED: {
    zh: "處理附件逾時，請重試。",
    en: "Processing the attachment timed out; please retry.",
  },
  SEARCH_PLAN_INVALID: {
    zh: "無法從檔案產生查詢，請重試。",
    en: "Could not build a lookup from the file; please retry.",
  },
  SEARCH_QUERY_REJECTED: {
    zh: "查詢內容不符規定，已停止。",
    en: "The lookup was rejected and stopped.",
  },
  ATTACHMENT_READ_FAILED: {
    zh: "讀取附件失敗，請重試。",
    en: "Reading the attachment failed; please retry.",
  },
  DB_EVIDENCE_FAILED: {
    zh: "查詢產品資料失敗，請重試。",
    en: "Looking up product data failed; please retry.",
  },
  SEARCH_FAILED: {
    zh: "外部查詢失敗，請重試。",
    en: "The external lookup failed; please retry.",
  },
  SEARCH_NOT_OBSERVED: {
    zh: "無法確認查詢來源，已停止。",
    en: "The lookup source could not be confirmed, so the answer was stopped.",
  },
  FINAL_ANSWER_FAILED: {
    zh: "產生答案失敗，請重試。",
    en: "Generating the answer failed; please retry.",
  },
  INVALID_ATTACHMENT_REQUEST: {
    zh: "附件請求格式不正確。",
    en: "The attachment request was not valid.",
  },
};
export function errorText(error, language = "zh") {
  return (
    ERROR_TEXT[error?.code]?.[language === "en" ? "en" : "zh"] || error?.message
  );
}
export function AttachmentError({ error, language = "zh" }) {
  if (!error) return null;
  const en = language === "en";
  const text = errorText(error, language);
  return (
    <div role="alert" className="text-red-700 dark:text-red-300 text-xs my-1">
      {text}
      {error.limit &&
        ` (${formatLimit(error.limit.actual, error.limit.unit)} / ${en ? "limit" : "上限"} ${formatLimit(error.limit.value, error.limit.unit)})`}
    </div>
  );
}
const TYPE_LABEL = {
  "application/pdf": "PDF",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/webp": "WEBP",
};

// Opens an attached file, at `page` when given, in the page's right-hand viewer (a new tab on narrow screens).
// Resolves false when this page holds no copy and Root can't send one: expired, removed, or no way to ask.
export async function openAttachment(panel, id, name, page = null) {
  const local =
    localFile(id) || (hasOriginalLoader() ? await ensureLocalFile(id) : null);
  if (!local) return false;
  const link = {
    title: page ? `${name}, p.${page}` : name,
    url: local.url,
    page,
  };
  if (
    panel?.show &&
    (window.matchMedia?.("(min-width: 768px)").matches ?? true)
  )
    panel.show(link);
  else
    window.open(
      `${local.url}${page ? `#page=${page}` : ""}`,
      "_blank",
      "noopener",
    );
  return true;
}

// More images than this fold into the first three and a "+N" tile.
const MAX_IMAGE_TILES = 4;

// What an image tile and a file chip share: the page an [a1] citation reports, opening the file, and whether it
// can still be opened. Root keeps an original for a limited time, so a file it no longer has stays listed, dimmed.
function useMessageFile(file, current, hidden, onReveal) {
  const ref = useRef(null);
  const panel = useContext(PdfPanelContext);
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  const revealing = useRef(false);
  useEffect(() => {
    const element = ref.current;
    const locate = (event) => {
      setPage(event.detail.page);
      if (hidden) {
        revealing.current = true;
        onReveal();
      }
    };
    element?.addEventListener("attachment-citation", locate);
    return () => element?.removeEventListener("attachment-citation", locate);
  }, [hidden, onReveal]);
  // A citation found this file folded under "+N", where scrolling to it does nothing: scroll again once it shows.
  useEffect(() => {
    if (hidden || !revealing.current) return;
    revealing.current = false;
    ref.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    ref.current?.focus();
  }, [hidden]);
  const gone =
    missing || (!!current && (isGone(current) || current.status === "expired"));
  const open = async () => {
    if (!(await openAttachment(panel, file.id, file.name))) setMissing(true);
  };
  return { ref, page, gone, open };
}

const goneText = (en) => (en ? "No longer available" : "原檔已無法開啟");
const TILE_FOCUS =
  "scroll-mt-8 outline-none focus-visible:ring-2 focus-visible:ring-orange-500";

function ImageTile({ file, current, targetId, language, hidden, onReveal }) {
  const en = language === "en";
  const { ref, gone, open } = useMessageFile(file, current, hidden, onReveal);
  return (
    <button
      ref={ref}
      id={targetId}
      type="button"
      hidden={hidden}
      disabled={gone}
      onClick={open}
      title={gone ? `${file.name} · ${goneText(en)}` : file.name}
      aria-label={
        gone
          ? `${file.name}, ${goneText(en)}`
          : `${en ? "Open" : "開啟"} ${file.name}`
      }
      className={`flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-transform enabled:hover:scale-105 disabled:cursor-default disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 ${TILE_FOCUS}`}
    >
      <FileThumb id={file.id} mimeType={file.mimeType} size="h-full w-full" />
    </button>
  );
}

function FileChip({ file, current, targetId, language }) {
  const en = language === "en";
  const { ref, page, gone, open } = useMessageFile(file, current, false, null);
  return (
    <button
      ref={ref}
      id={targetId}
      type="button"
      disabled={gone}
      onClick={open}
      title={gone ? `${file.name} · ${goneText(en)}` : file.name}
      aria-label={
        gone
          ? `${file.name}, ${goneText(en)}`
          : `${en ? "Open" : "開啟"} ${file.name}`
      }
      className={`inline-flex max-w-full items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-800 shadow-sm transition-transform enabled:hover:scale-[1.02] disabled:cursor-default disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${TILE_FOCUS}`}
    >
      <FileIcon mimeType={file.mimeType} />
      <span className="min-w-0 truncate font-medium">{file.name}</span>
      <span className="shrink-0 rounded bg-gray-100 px-1.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        {TYPE_LABEL[file.mimeType] || "FILE"}
      </span>
      {page != null && (
        <span
          className="shrink-0 text-[11px] text-orange-600 dark:text-orange-300"
          aria-live="polite"
        >
          p.{page}
        </span>
      )}
      {gone && (
        <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
          {goneText(en)}
        </span>
      )}
    </button>
  );
}

// The files of the user's own message, shown above its text: images as thumbnail tiles, other files (PDFs) as
// chips. Clicking one opens it in the right-hand viewer. Each keeps the id an [a1] citation scrolls to, at
// `${targetPrefix}-${file.id}`; one folded under "+N" unfolds the tiles when a citation points at it.
export function MessageFiles({ files, current = [], targetPrefix, language }) {
  const [expanded, setExpanded] = useState(false);
  const reveal = useCallback(() => setExpanded(true), []);
  const isImage = (file) => String(file.mimeType).startsWith("image/");
  const images = files.filter(isImage);
  const others = files.filter((file) => !isImage(file));
  const folded = !expanded && images.length > MAX_IMAGE_TILES;
  const shown = folded ? MAX_IMAGE_TILES - 1 : images.length;
  const shared = (file) => ({
    file,
    current: current.find((item) => item.id === file.id),
    targetId: `${targetPrefix}-${file.id}`,
    language,
  });
  return (
    <div className="flex max-w-[85%] md:max-w-[70%] flex-col items-end gap-1.5">
      {images.length > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {images.map((file, index) => (
            <ImageTile
              key={file.id}
              {...shared(file)}
              hidden={index >= shown}
              onReveal={reveal}
            />
          ))}
          {folded && (
            <button
              type="button"
              onClick={reveal}
              aria-label={
                language === "en"
                  ? `Show ${images.length - shown} more images`
                  : `再顯示 ${images.length - shown} 張圖片`
              }
              className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-100 text-lg font-semibold text-gray-700 shadow-sm transition-transform hover:scale-105 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100 ${TILE_FOCUS}`}
            >
              +{images.length - shown}
            </button>
          )}
        </div>
      )}
      {others.map((file) => (
        <FileChip key={file.id} {...shared(file)} />
      ))}
    </div>
  );
}

const MENU_ITEM =
  "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 transition-colors";

// The plus button and its two-item menu. The notice and the file input live in useUploadGate.
export function AttachmentMenu({
  disabled,
  language,
  loading,
  onPick,
  onInfo,
}) {
  const zh = language === "zh";
  const [open, setOpen] = useState(false);
  const menu = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (event) => {
      if (
        event.key === "Escape" ||
        (event.type === "pointerdown" && !menu.current?.contains(event.target))
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  // The capability call can take a while on a cold server: show the button right away, busy and unclickable.
  if (loading)
    return (
      <div className="relative shrink-0">
        <button
          type="button"
          disabled
          aria-label={zh ? "正在連線附件功能…" : "Connecting attachments…"}
          title={zh ? "正在連線附件功能…" : "Connecting attachments…"}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-black/30 dark:border-white/30 bg-slate-100 text-gray-500 cursor-wait"
        >
          <FiLoader className="h-5 w-5 animate-spin" aria-hidden="true" />
        </button>
      </div>
    );
  return (
    <div ref={menu} className="relative shrink-0">
      <button
        type="button"
        aria-label="新增附件"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-black/30 dark:border-white/30 bg-slate-100 text-gray-700 hover:bg-white hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        <FiPlus className="h-5 w-5" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="附件選單"
          className="absolute bottom-full left-0 mb-2 z-20 w-48 rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          <button
            type="button"
            role="menuitem"
            className={MENU_ITEM}
            onClick={() => {
              setOpen(false);
              onPick();
            }}
          >
            <FiUpload className="h-4 w-4" />
            {zh ? "上傳檔案" : "Upload files"}
          </button>
          <button
            type="button"
            role="menuitem"
            className={MENU_ITEM}
            onClick={() => {
              setOpen(false);
              onInfo();
            }}
          >
            <FiInfo className="h-4 w-4" />
            {zh ? "上傳須知" : "Upload notice"}
          </button>
        </div>
      )}
    </div>
  );
}
const BUTTON =
  "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";
const BUTTON_OUTLINE = `${BUTTON} border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700`;
const BUTTON_PRIMARY = `${BUTTON} border border-orange-500 bg-orange-500 text-white hover:bg-orange-600`;
const BUTTON_DANGER = `${BUTTON} border border-red-500 bg-red-500 text-white hover:bg-red-600`;
// One compact pill per file; errors and confirmations hang below it.
const PILL_SHAPE =
  "relative flex max-w-full items-center gap-2 overflow-hidden rounded-full border py-1.5 pl-2 pr-1.5 text-xs text-gray-800 shadow-sm dark:text-gray-100";
const PILL = `${PILL_SHAPE} border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800`;
// A file that goes with the next message.
const PILL_ON = `${PILL_SHAPE} border-orange-500 bg-orange-50 dark:bg-orange-500/10`;
const CLOSE =
  "shrink-0 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white cursor-pointer disabled:opacity-50";

const TEXT = {
  zh: {
    attach: "附在下一則提問",
    waiting: "等待上傳",
    uploading: "上傳中",
    processing: "處理中…",
    cancelled: "已取消",
    failed: "上傳失敗",
    retry: "重試",
    cancel: "取消",
    expired: "已過期，請重新上傳",
    unverified: "正在核對原檔…",
    removeAsk: "移除這個檔案？",
    remove: "移除",
    keep: "取消",
    removeLocalAsk: "移除這筆上傳記錄？",
    quota: (turn, turnMax, session, sessionMax) =>
      `本輪 ${turn} / ${turnMax} · 本對話已用 ${session} / ${sessionMax}`,
    refresh: "重新整理",
    loading: "正在確認附件功能…",
    off: "附件功能未啟用，可繼續文字聊天。",
    unavailable: (message) => `附件暫時無法使用：${message}。可繼續文字聊天。`,
  },
  en: {
    attach: "Attach to the next message",
    waiting: "Waiting",
    uploading: "Uploading",
    processing: "Processing…",
    cancelled: "Cancelled",
    failed: "Upload failed",
    retry: "Retry",
    cancel: "Cancel",
    expired: "Expired — please upload it again",
    unverified: "Checking the file…",
    removeAsk: "Remove this file?",
    remove: "Remove",
    keep: "Cancel",
    removeLocalAsk: "Remove this upload entry?",
    quota: (turn, turnMax, session, sessionMax) =>
      `This turn ${turn} / ${turnMax} · this chat ${session} / ${sessionMax}`,
    refresh: "Refresh",
    loading: "Checking attachments…",
    off: "Attachments are not enabled; text chat still works.",
    unavailable: (message) =>
      `Attachments are unavailable: ${message}. Text chat still works.`,
  },
};

function FileIcon({ mimeType }) {
  const Icon = String(mimeType).startsWith("image/") ? FiImage : FiFileText;
  return (
    <Icon className="h-4 w-4 shrink-0 text-orange-500" aria-hidden="true" />
  );
}

// An image shows itself, small; everything else (and an image this page no longer holds) shows the icon.
// `file` is a File still being uploaded; `id` is an uploaded one, looked up among the files this page picked.
export function FileThumb({ id, file, mimeType, size = "h-8 w-8" }) {
  const [own, setOwn] = useState(null);
  useSyncExternalStore(subscribeLocalFiles, localFilesVersion);
  const isImage = String(mimeType).startsWith("image/");
  useEffect(() => {
    if (id && !file && isImage && !localFile(id)) ensureLocalFile(id);
  }, [id, file, isImage]);
  useEffect(() => {
    if (
      !file ||
      typeof URL.createObjectURL !== "function" ||
      !String(mimeType).startsWith("image/")
    )
      return undefined;
    const url = URL.createObjectURL(file);
    setOwn(url);
    return () => {
      if (typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
    };
  }, [file, mimeType]);
  const url =
    own ||
    (id && String(mimeType).startsWith("image/") ? localFile(id)?.url : null);
  if (!url) return <FileIcon mimeType={mimeType} />;
  return (
    <img
      src={url}
      alt=""
      data-testid="file-thumbnail"
      className={`${size} shrink-0 rounded object-cover`}
    />
  );
}

function RemoveConfirm({
  text,
  label,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  disabled,
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-2 py-1.5 dark:bg-red-500/10">
      <span className="text-red-700 dark:text-red-300">{text}</span>
      <button
        type="button"
        aria-label={confirmLabel}
        disabled={disabled}
        className={BUTTON_DANGER}
        onClick={onConfirm}
      >
        {label.confirm}
      </button>
      <button
        type="button"
        aria-label={cancelLabel}
        className={BUTTON_OUTLINE}
        onClick={onCancel}
      >
        {label.cancel}
      </button>
    </div>
  );
}

// A short notice when a file is turned away, so the user does not have to notice the red line under the chip.
// role="status", not "alert": the line under the chip is already the alert.
function RejectionToast({ pending, language }) {
  const [toast, setToast] = useState(null);
  const told = useRef(new Map());
  useEffect(() => {
    const item = pending.find(
      (entry) =>
        entry.status === "error" &&
        entry.error &&
        told.current.get(entry.key) !== entry.error,
    );
    if (!item) return undefined;
    told.current.set(item.key, item.error);
    setToast({
      name: item.name,
      text: errorText(item.error, language),
      limit: item.error.limit,
    });
    return undefined;
  }, [pending, language]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(timer);
  }, [toast]);
  if (!toast) return null;
  return (
    <div
      role="status"
      data-testid="upload-rejected-toast"
      className="fixed right-4 top-4 z-50 flex max-w-sm items-start gap-3 rounded-xl border border-red-300 bg-white px-4 py-3 text-sm text-red-700 shadow-lg dark:border-red-500/40 dark:bg-gray-800 dark:text-red-300"
    >
      <div className="min-w-0">
        <p className="font-semibold">
          {language === "en"
            ? "This file cannot be attached"
            : "這個檔案無法附加"}
        </p>
        <p className="truncate" title={toast.name}>
          {toast.name}
        </p>
        <p>
          {toast.text}
          {toast.limit &&
            ` (${formatLimit(toast.limit.actual, toast.limit.unit)} / ${language === "en" ? "limit" : "上限"} ${formatLimit(toast.limit.value, toast.limit.unit)})`}
        </p>
      </div>
      <button
        type="button"
        aria-label={language === "en" ? "Dismiss" : "關閉通知"}
        onClick={() => setToast(null)}
        className="shrink-0 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <FiX className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function AttachmentPanel({
  attachments,
  model,
  includeVideo,
  busy,
  language = "zh",
}) {
  const {
    controller,
    capability,
    files,
    selected,
    pending,
    checking,
    sessionUsageBytes,
    error,
  } = attachments;
  const t = TEXT[language === "en" ? "en" : "zh"];
  const [removing, setRemoving] = useState(null);
  useEffect(() => {
    setRemoving(null);
  }, [controller, attachments.sessionId]);
  if (!capability)
    return (
      <p
        className="w-full text-xs text-gray-500 dark:text-gray-400"
        role="status"
      >
        {checking ? t.loading : error ? t.unavailable(error.message) : ""}
      </p>
    );
  if (!capability.enabled)
    return (
      <p
        className="w-full text-xs text-gray-500 dark:text-gray-400"
        role="status"
      >
        {t.off}
      </p>
    );
  // A removed file is gone: it leaves the list instead of lingering as a disabled card.
  const visible = files.filter((file) => !isGone(file));
  const now = controller.now();
  const selectedBytes = visible
    .filter((file) => selected.includes(file.id))
    .reduce((sum, file) => sum + file.sizeBytes, 0);
  const validation = controller.validation(model, includeVideo);
  const confirmLabels = { confirm: t.remove, cancel: t.keep };
  // Nothing to show: no empty row at the top of the chat box.
  if (!visible.length && !pending.length && !validation && !error) return null;
  return (
    <div className="w-full">
      <RejectionToast pending={pending} language={language} />
      <div
        aria-label="附件清單"
        className="flex flex-wrap items-start gap-2 mb-2"
      >
        {visible.map((file) => {
          const ready = isReady(file, now);
          const attached = selected.includes(file.id);
          const status = ready
            ? formatExpiry(file.expiresAt, now, language)
            : file.status === "unverified"
              ? t.unverified
              : t.expired;
          // Size and expiry live in the tooltip; only a file that can't be sent keeps its status in view.
          const detail = `${file.name} · ${formatBytes(file.sizeBytes)} · ${status}`;
          return (
            <div key={file.id} className="flex max-w-full flex-col gap-1">
              <div
                title={detail}
                className={`${ready && attached ? PILL_ON : PILL} ${ready ? "" : "opacity-70"}`}
              >
                {ready ? (
                  // The chip is the switch: tap it to send the file with the next message, or not.
                  <button
                    type="button"
                    aria-pressed={attached}
                    aria-label={`${t.attach}: ${detail}`}
                    disabled={busy || checking}
                    onClick={() => controller.toggle(file.id)}
                    className="flex min-w-0 items-center gap-2 rounded-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                        attached
                          ? "border-orange-500 bg-orange-500 text-white"
                          : "border-gray-400 text-transparent"
                      }`}
                    >
                      <FiCheck className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <FileThumb id={file.id} mimeType={file.mimeType} />
                    <strong className="min-w-0 max-w-[11rem] truncate text-xs font-medium">
                      {file.name}
                    </strong>
                  </button>
                ) : (
                  <>
                    <FileThumb id={file.id} mimeType={file.mimeType} />
                    <strong className="min-w-0 max-w-[11rem] truncate text-xs font-medium">
                      {file.name}
                    </strong>
                    <span className="shrink-0 text-gray-500 dark:text-gray-400">
                      {status}
                    </span>
                  </>
                )}
                <button
                  type="button"
                  aria-label={`移除 ${file.name}`}
                  title={t.remove}
                  disabled={busy}
                  onClick={() => setRemoving(file.id)}
                  className={CLOSE}
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
              {removing === file.id && (
                <RemoveConfirm
                  text={t.removeAsk}
                  label={confirmLabels}
                  confirmLabel="確認移除"
                  cancelLabel="取消移除"
                  disabled={busy}
                  onConfirm={() => {
                    controller.remove(file.id);
                    setRemoving(null);
                  }}
                  onCancel={() => setRemoving(null)}
                />
              )}
            </div>
          );
        })}
        {pending.map((item) => {
          const active = ["pending", "uploading"].includes(item.status);
          const failed = ["error", "cancelled"].includes(item.status);
          return (
            <div key={item.key} className="flex max-w-full flex-col gap-1">
              <div className={PILL}>
                <FileThumb file={item.file} mimeType={item.file.type} />
                <strong
                  className="min-w-0 max-w-[11rem] truncate text-xs font-medium"
                  title={item.name}
                >
                  {item.name}
                </strong>
                <span className="shrink-0 text-gray-500 dark:text-gray-400">
                  {formatBytes(item.file.size)} ·{" "}
                  {item.status === "uploading"
                    ? item.progress >= 100
                      ? t.processing
                      : `${t.uploading} ${item.progress}%`
                    : item.status === "pending"
                      ? t.waiting
                      : item.status === "cancelled"
                        ? t.cancelled
                        : t.failed}
                </span>
                {active && (
                  <button
                    type="button"
                    aria-label={`取消 ${item.name}`}
                    className={`${BUTTON_OUTLINE} !py-0.5`}
                    onClick={() => controller.cancel(item.key)}
                  >
                    {t.cancel}
                  </button>
                )}
                {failed && !RETRY_USELESS.has(item.error?.code) && (
                  <button
                    type="button"
                    aria-label={`重試 ${item.name}`}
                    disabled={busy}
                    className={`${BUTTON_PRIMARY} !py-0.5`}
                    onClick={() => controller.upload(item.file, item.key)}
                  >
                    {t.retry}
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`移除待上傳 ${item.name}`}
                  title={t.remove}
                  onClick={() => setRemoving(item.key)}
                  className={CLOSE}
                >
                  <FiX className="h-4 w-4" />
                </button>
                {active && (
                  <div
                    role="progressbar"
                    aria-label={`${item.name} 上傳進度`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={item.progress}
                    className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-200 dark:bg-gray-700"
                  >
                    <div
                      className={`h-full bg-orange-500 transition-all ${item.progress >= 100 ? "animate-pulse" : ""}`}
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                )}
              </div>
              <AttachmentError error={item.error} language={language} />
              {removing === item.key && (
                <RemoveConfirm
                  text={t.removeLocalAsk}
                  label={confirmLabels}
                  confirmLabel="確認移除記錄"
                  cancelLabel="取消移除記錄"
                  onConfirm={() => {
                    controller.discardPending(item.key);
                    setRemoving(null);
                  }}
                  onCancel={() => setRemoving(null)}
                />
              )}
            </div>
          );
        })}
      </div>
      {(visible.length > 0 || pending.length > 0) && (
        <p className="mb-1 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          {t.quota(
            formatBytes(selectedBytes),
            formatBytes(capability.limits.turnBytes),
            formatBytes(sessionUsageBytes),
            formatBytes(capability.limits.sessionBytes),
          )}
          <button
            type="button"
            disabled={busy}
            className="rounded-full px-2 py-0.5 text-orange-600 underline hover:bg-orange-50 dark:text-orange-300 dark:hover:bg-orange-500/10 cursor-pointer"
            onClick={() =>
              controller.refresh().catch((e) =>
                controller.patch({
                  error: e.detail || {
                    message: e.message,
                  },
                }),
              )
            }
          >
            {t.refresh}
          </button>
        </p>
      )}
      <AttachmentError error={validation || error} language={language} />
    </div>
  );
}
// The app's own sentence in the page language; Root's reason (a server note,
// in English) is only the tooltip.
export function ModelNotices({ capability, model, language = "zh" }) {
  if (!capability?.enabled) return null;
  const chosen = capability.models?.find((item) => item.id === model);
  if (!chosen || chosen.supported) return null;
  return (
    <p
      className="text-xs my-2 text-red-700 dark:text-red-400"
      title={chosen.reason || undefined}
    >
      {language === "en"
        ? `The selected model ${chosen.id} can't read attachments. Pick another model, or deselect the files.`
        : `目前的模型 ${chosen.id} 無法讀取附件；請改選其他模型或取消附件。`}
    </p>
  );
}
